import CheckboxComponent from '../../../../components/CheckboxComponent';
import SelectorEngine from '../../../../utils/dom/selector-engine';
import { ClassNames } from '../../../../utils/dom/css-classes';
import Request from '../../../../utils/request';
import { startLoadingAnimation, stopLoadingAnimation } from '../../../../utils/dom/loading';

/**
 * Roles checkbox that loads the contact's existing roles on step 2.
 * Pass `contactId` to `api.url`. Mutually exclusive role rules still use
 * `mutuallyExclusiveRules` from the checkbox config.
 */
class ContactRolesCheckbox extends CheckboxComponent {
	constructor(containerElement, config) {
		super(containerElement, config);
		this._loadedContactId = null;
		this.init();
	}

	getRolesApiUrl() {
		return this.data?.api?.url || '/api/venueRegistration/teamInformation/members/roles';
	}

	getContactId() {
		if (typeof this.data?.getContactId === 'function') {
			const contactId = this.data.getContactId();
			if (contactId) {
				return contactId;
			}
		}

		const selectedContact = this.data?.getSelectedContact?.();
		if (selectedContact?.id) {
			return selectedContact.id;
		}

		const answers = this.data?.getFormAnswers?.() || {};
		for (const stepAnswers of Object.values(answers)) {
			for (const answer of Object.values(stepAnswers || {})) {
				if (answer?.fieldName === 'contactId' && answer?.value) {
					return answer.value;
				}

				if (answer?.selectedContact?.id) {
					return answer.selectedContact.id;
				}
			}
		}

		return null;
	}

	normaliseRoleIds(response) {
		if (Array.isArray(response?.roles)) {
			return response.roles;
		}

		if (Array.isArray(response?.value)) {
			return response.value.map((item) => item?.id || item).filter(Boolean);
		}

		if (typeof response?.value === 'string' && response.value) {
			return response.value.split(',').map((role) => role.trim()).filter(Boolean);
		}

		return [];
	}

	async init() {
		const contactId = this.getContactId();
		if (!contactId || contactId === this._loadedContactId) {
			return;
		}

		this._loadedContactId = contactId;
		startLoadingAnimation();

		try {
			const response = await Request.get(this.getRolesApiUrl(), { contactId });
			this.applySelectedRoles(this.normaliseRoleIds(response));
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('Failed to load contact roles:', error);
			this._loadedContactId = null;
		} finally {
			stopLoadingAnimation();
		}
	}

	applySelectedRoles(roleIds = []) {
		this.data.value = roleIds;

		const checkboxes = SelectorEngine.find('.js-option-checkbox', this.containerEl);
		for (const checkbox of checkboxes) {
			checkbox.checked = roleIds.includes(checkbox.value);
		}

		this.updateSelectAllState();
		// Pre-fill from the API without treating an empty selection as a submit error.
		this.handleCheckboxClick(false);
		this.clearValidationMessage();
	}

	clearValidationMessage() {
		const errorElement = SelectorEngine.findOne('#lta-radio-checkbox-error', this.containerEl);
		const inputGroupElement = SelectorEngine.findOne('.lta-form-check-group', this.containerEl);

		errorElement?.classList.add(ClassNames.Hidden);
		inputGroupElement?.classList.remove(ClassNames.Invalid, ClassNames.Focused);
	}
}

export default ContactRolesCheckbox;
